import { useEffect, useRef, useState } from 'react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import {
  AlertTriangle,
  CalendarOff,
  CircleCheck,
  GripVertical,
  X,
} from 'lucide-react'
import { menuProductState } from '../menu-diff'
import type { MissingMenuProduct } from '../menu-diff'
import type { TileState } from '../constants'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'

/** The badge a row carries, if any — `ready` and `open-price` need none to place safely. */
const ROW_BADGES: Partial<Record<TileState, string>> = {
  'no-price': 'tileState.noPrice',
  deactivated: 'tileState.inactive',
  'not-assigned': 'tileState.notAssigned',
}

function formatDay(isoDate: string) {
  // The T00:00:00 keeps a date-only value from sliding a day backwards in a western timezone.
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('de-AT', {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  })
}

function MissingRow({
  product,
  onPlace,
  canEdit,
  canPlace,
}: {
  product: MissingMenuProduct
  onPlace: (product: MissingMenuProduct) => void
  canEdit: boolean
  canPlace: boolean
}) {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const rowRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const state = menuProductState(product)
  const badgeKey = ROW_BADGES[state]

  useEffect(() => {
    if (!canEdit) return
    const el = rowRef.current
    invariant(el)
    return draggable({
      element: el,
      // The same payload a library row carries, so a drop behaves identically — including the
      // prompt to activate a deactivated article. A merged duplicate hands over its primary.
      getInitialData: () => ({
        type: 'product',
        sku: product.placementSku,
        name: product.label,
        active: product.active,
      }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    })
  }, [product, canEdit])

  return (
    <div
      ref={rowRef}
      className={`flex items-start gap-2.5 py-3 transition-opacity ${
        dragging ? 'opacity-60' : ''
      }`}
    >
      <GripVertical
        className={`mt-0.5 h-4 w-4 shrink-0 ${canEdit ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{product.label}</div>
        <div className="truncate text-xs text-muted-foreground">
          {tPosLayout('labels.sku')} {product.placementSku}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {product.days.map((day) => (
            <Badge
              key={day.menuId}
              variant="outline"
              className={
                day.status === 'draft'
                  ? 'border-dashed text-muted-foreground'
                  : undefined
              }
            >
              {day.status === 'draft'
                ? tPosLayout('menuDiff.dayDraft', {
                    day: formatDay(day.menuDate),
                  })
                : formatDay(day.menuDate)}
            </Badge>
          ))}
          {badgeKey && (
            <Badge variant="secondary">{tPosLayout(badgeKey)}</Badge>
          )}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => onPlace(product)}
        disabled={!canEdit || !canPlace}
      >
        {tPosLayout('menuDiff.place')}
      </Button>
    </div>
  )
}

interface MissingFromLayoutPanelProps {
  open: boolean
  onClose: () => void
  items: Array<MissingMenuProduct>
  loading: boolean
  /**
   * How many products the menus in the window hold at all.
   *
   * Without it an empty list cannot say whether every planned article is placed or whether nothing
   * is planned — and reporting "all placed" for a restaurant with no menu at all is the kind of
   * reassuring lie that made this panel look broken in the first place.
   */
  plannedCount: number
  /** The menus could not be read; an empty list then means nothing at all. */
  failed?: boolean
  /** End of the window the diff covers, ISO date — the start is always today. */
  windowTo?: string
  /** Name of the page a placement would land on; absent when no page is selected. */
  targetPageName?: string
  onPlace: (product: MissingMenuProduct) => void
  canEdit: boolean
}

/**
 * Products planned on the coming days' menus that no tile covers
 * (CHG-2026-09-pos-layout-stock-and-menu-diff).
 *
 * Shares the editor's right-hand column with the product library: both answer "what do I place
 * next", and a second column would halve the grid this list is talking about.
 */
export function MissingFromLayoutPanel({
  open,
  onClose,
  items,
  loading,
  plannedCount,
  failed = false,
  windowTo,
  targetPageName,
  onPlace,
  canEdit,
}: MissingFromLayoutPanelProps) {
  const { t: tPosLayout } = useTranslation('pos-layout')

  return (
    <aside
      aria-hidden={!open}
      className={`flex h-full flex-col rounded-xl border bg-card shadow-sm transition-all duration-300 ${
        open
          ? 'opacity-100 translate-x-0'
          : 'pointer-events-none opacity-0 translate-x-6'
      }`}
    >
      <div className="flex items-start justify-between border-b px-4 py-3">
        <div>
          <div className="text-sm font-semibold">
            {tPosLayout('menuDiff.title')}
          </div>
          <div className="text-xs text-muted-foreground">
            {windowTo
              ? tPosLayout('menuDiff.window', { to: formatDay(windowTo) })
              : tPosLayout('menuDiff.subtitle')}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={tPosLayout('menuDiff.closeAria')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 pb-4">
        <ScrollArea className="h-full overflow-hidden">
          <div className="px-4">
            {loading ? (
              <div className="py-4 text-sm text-muted-foreground">
                {tPosLayout('menuDiff.loading')}
              </div>
            ) : failed ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div className="text-sm font-medium">
                  {tPosLayout('menuDiff.failedTitle')}
                </div>
                <div className="max-w-[16rem] text-xs text-muted-foreground">
                  {tPosLayout('menuDiff.failedBody')}
                </div>
              </div>
            ) : plannedCount === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <CalendarOff className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm font-medium">
                  {tPosLayout('menuDiff.noMenusTitle')}
                </div>
                <div className="max-w-[16rem] text-xs text-muted-foreground">
                  {windowTo
                    ? tPosLayout('menuDiff.noMenusBody', {
                        to: formatDay(windowTo),
                      })
                    : tPosLayout('menuDiff.subtitle')}
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <CircleCheck className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm font-medium">
                  {tPosLayout('menuDiff.emptyTitle')}
                </div>
                <div className="max-w-[16rem] text-xs text-muted-foreground">
                  {windowTo
                    ? tPosLayout('menuDiff.emptyBody', {
                        to: formatDay(windowTo),
                        count: plannedCount,
                      })
                    : tPosLayout('menuDiff.subtitle')}
                </div>
              </div>
            ) : (
              <>
                <div className="divide-y divide-border/60">
                  {items.map((product) => (
                    <MissingRow
                      key={product.placementSku}
                      product={product}
                      onPlace={onPlace}
                      canEdit={canEdit}
                      canPlace={!!targetPageName}
                    />
                  ))}
                </div>
                {targetPageName && (
                  <p className="pt-3 text-xs leading-relaxed text-muted-foreground">
                    {tPosLayout('menuDiff.placementHint', {
                      page: targetPageName,
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </aside>
  )
}
